import PropTypes from 'prop-types'
import { Chip, Tooltip } from '@mui/material'

import useTranslations from '../../../i18n/useTranslations'

import { ReactComponent as StepCancelledStatusChip } from '../../../assets/icons/step-cancelled-status-chip.svg'
import { ReactComponent as WarningFill } from '../../../assets/icons/warning-fill.svg'
import { ReactComponent as Edit } from '../../../assets/icons/edit.svg'
import { ReactComponent as Resolve } from '../../../assets/icons/edit.svg'
import { ReactComponent as ReturnArrow } from '../../../assets/icons/return-arrow.svg'
import { ReactComponent as Success } from '../../../assets/icons/success.svg'
import {
  INCIDENTS_CODES_ALLOWED_FOR_RESOLUTION,
  SHIPPING_STATUS_CODES,
} from '../../../common/constants'
import { useHistory } from 'react-router-dom'
import { useUserContext } from '../../../auth/UserProvider'

const StatusCodeChip = ({
  state,
  size = 'small',
  hasIncident,
  isCancelled,
  isUnscheduledPickup,
  isRequestedReturn,
  id,
  incidentDesc,
  ...muiProps
}) => {
  const formatMessage = useTranslations()
  const t = useTranslations()
  const history = useHistory()
  const { centres } = useUserContext()
  const formatIncDesc = incidentDesc
    ? incidentDesc.charAt(0).toUpperCase() + incidentDesc.slice(1).toLowerCase()
    : null
  // state.canResolve solo está presente cuando `state` viene del envío
  // "actual" (tracking_info ya resuelto por backend, ver parseShipping /
  // mergeTrackingInfoIntoShipping). El histórico completo de eventos
  // (TrackingTable) construye su propio `state` por evento sin ese campo, así
  // que conserva la heurística antigua basada en código de estado.
  const isSolvable =
    state?.canResolve !== undefined
      ? Boolean(state.canResolve)
      : (['WEB1700', 'WEB1800'].includes(state?.lastStatusCode) &&
          hasIncident &&
          state?.severity === 'primary') ||
        (INCIDENTS_CODES_ALLOWED_FOR_RESOLUTION.includes(state?.incidentCode) &&
          (state?.lastStatusCode === SHIPPING_STATUS_CODES.incidentDelivering ||
            state?.lastStatusCode === SHIPPING_STATUS_CODES.delivering))
  const isInactiveCenter = centres?.find(
    centre =>
      centre?.id === state?.clientCenterCode && centre?.status !== 'ACTIVE',
  )
  const formatName = name =>
    isUnscheduledPickup ? formatMessage('tracking-line:pickup-unscheduled') : name

  return (
    <Tooltip
      disableHoverListener={
        isCancelled ||
        !(
          hasIncident ||
          (hasIncident && state?.severity !== 'orange' && isSolvable) ||
          isUnscheduledPickup
        )
      }
      title={
        !isCancelled &&
          hasIncident &&
          state?.severity !== 'orange' &&
          isSolvable
          ? t('tooltip:resoluble')
          : isUnscheduledPickup && isInactiveCenter
            ? t('tooltip:not-schedule')
            : isUnscheduledPickup
              ? t('tooltip:schedule')
              : hasIncident && state?.severity !== 'orange'
                ? t('tooltip:not-resoluble')
                : t('tooltip:solved')
      }
      arrow
      placement="top"
    >
      <Chip
        icon={
          isCancelled ? (
            <StepCancelledStatusChip />
          ) : hasIncident && state?.severity !== 'orange' && isSolvable ? (
            <Resolve />
          ) : isUnscheduledPickup ? (
            <Edit />
          ) : hasIncident ||
            state?.severity === 'orange' ||
            ['WEB1600', 'WEB1700', 'WEB1800', 'WEB1900'].includes(
              state?.lastStatusCode,
            ) ? (
            <WarningFill />
          ) : isRequestedReturn &&
            SHIPPING_STATUS_CODES.delivered === state?.lastStatusCode ? (
            <Tooltip title={t('tooltip:return-shipment')} arrow placement="top">
              <ReturnArrow
                onClick={() =>
                  history.push(`/shippings-tracking/${isRequestedReturn}`)
                }
              ></ReturnArrow>
            </Tooltip>
          ) : state?.lastStatusCode === SHIPPING_STATUS_CODES.delivered ? (
            <Success />
          ) : null
        }
        size={size}
        classes={{
          root: isCancelled
            ? 'red'
            : isUnscheduledPickup
              ? 'yellow'
              : state?.lastStatusCode === SHIPPING_STATUS_CODES.delivered
                ? 'green'
                : state?.severity,
        }}
        label={
          isCancelled ? (
            <div
              onClick={() => {
                if (id) history.push(`/shippings-tracking/${id}`)
              }}
            >
              {formatMessage('tracking:cancelled')}
            </div>
          ) : (
            <div
              onClick={() => {
                if (id) history.push(`/shippings-tracking/${id}`)
              }}
            >
              {['WEB1700', 'WEB1800', 'WEB1900'].includes(state?.lastStatusCode) && !hasIncident
                ? state?.lastStatusDescription
                : formatIncDesc
                  ? `${formatName(state?.lastStatusDescription ?? '')}. ${formatIncDesc}`
                  : formatName(state?.lastStatusDescription ?? '')}
            </div>
          )
        }
        {...muiProps}
      />
    </Tooltip>
  )
}

StatusCodeChip.propTypes = {
  id: PropTypes.string,
  state: PropTypes.shape({
    shippingCode: PropTypes.string,
    clientCenterCode: PropTypes.string,
    lastStatusCode: PropTypes.string,
    lastStatusDescription: PropTypes.string,
    incidentCode: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    canResolve: PropTypes.bool,
    isResolved: PropTypes.bool,
    severity: PropTypes.string.isRequired,
  }),
  size: PropTypes.oneOf(['small', 'medium']),
  hasIncident: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isCancelled: PropTypes.bool,
  isUnscheduledPickup: PropTypes.bool,
  isRequestedReturn: PropTypes.bool,
  incidentDesc: PropTypes.string,
}

export default StatusCodeChip
